import { isEmpty, isEqual, isNil, omitBy } from 'lodash';
import moment from 'moment';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { View as RNView, ScrollView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import {
  AnchoredSheet,
  AnchoredSheetProps,
  AnchoredSheetTitleBar,
} from '@shared/components/AnchoredSheet';
import { MBView } from '@shared/components/MBView';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBCalendar } from '@shared/components/dateAndTime/calendar';
import {
  DATE_FORMATS,
  TIME_FORMATS,
} from '@shared/components/dateAndTime/comboDatePicker/constants';
import { MBComboDatePickerField } from '@shared/components/dateAndTime/comboDatePicker/field';
import { MBPlatformTimePicker } from '@shared/components/dateAndTime/timePicker/platform';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import {
  TimeTrackerButton,
  TimerText,
  calculateDurationFromTimeTrackerData,
  formatMillisecondsToDurationString,
  isTimerRunning,
} from '@shared/components/timer';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  TimeTrackerComponentData,
  TimeTrackerComponentDataValue,
} from '@shared/data/entity/fields/TimeTrackerField';
import { ColorThemeContext, Colors, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { ISOString } from '@shared/types/dates';
import { MBAlert } from '@shared/util/alert';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { isInputComponent } from '../../utils';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowComponentWrapper } from './WorkflowComponentWrapper';

//#region Constants

const DEFAULT_DATE_RANGE_INTERVAL_MS = 60 * 60 * 1000;

//#endregion

//#region Internal Types

type DateOrTime = 'date' | 'time';

type DatumEditorSelectionState = {
  inputType: DateOrTime;
  rangeSide: 'start' | 'end';
};

type DatumEditorState = { start: Date; end: Date } | null;

type LogDatumEditorPresentationState =
  | { type: 'manual' }
  | { type: 'edit'; datum: TimeTrackerComponentDataValue; index: number };

//#endregion

//#region Component

export interface WorkflowTimeTrackerComponentProps extends WorkflowComponentProps {
  initialData?: TimeTrackerComponentData;
}

export function WorkflowTimeTrackerComponent({
  initialData,
  component,
  onDataChanged,
  showLabel,
  paddingHorizontal,
  iconPad,
  isReadOnly: _isReadOnly,
  overrides,
}: WorkflowTimeTrackerComponentProps) {
  const timeTrackerComponent = component as API.TimeTrackerComponent;
  const isReadOnly = _isReadOnly || timeTrackerComponent.readonly;

  const anchorName = getAnchorName('time-tracker', component.id);
  const [data, _setData] = useState<TimeTrackerComponentData>(initialData ?? []);
  const [showLog, setShowLog] = useState(false);
  const onShowLog = useCallback(() => setShowLog(true), [setShowLog]);
  const onCloseLog = useCallback(() => setShowLog(false), [setShowLog]);
  const componentWithOverrides = {
    ...timeTrackerComponent,
    ...omitBy(overrides, isNil),
  };

  const setData = useCallback(
    (newData: TimeTrackerComponentData) => {
      _setData(newData);
      // @ts-expect-error -- strict migration
      onDataChanged(component.id, newData);
    },
    [component.id, onDataChanged],
  );

  useEffect(() => {
    if (componentWithOverrides.autoStart && isEmpty(initialData) && !isReadOnly) {
      setData([{ start: { timestamp: new Date().toISOString() } }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentWithOverrides]);

  if (!isInputComponent(component)) {
    return null;
  }

  return (
    <WorkflowComponentWrapper
      // @ts-expect-error -- strict migration
      paddingHorizontal={paddingHorizontal}
      // @ts-expect-error -- strict migration
      iconPad={iconPad}
      component={componentWithOverrides}
      // @ts-expect-error -- strict migration
      showLabel={showLabel}
      showIcon={false}
    >
      <Timer
        component={componentWithOverrides}
        data={data}
        // @ts-expect-error -- strict migration
        isReadOnly={isReadOnly}
        anchorName={anchorName}
        onDataChanged={setData}
        onShowLog={onShowLog}
      />
      {showLog && (
        <Log
          component={componentWithOverrides}
          data={data}
          // @ts-expect-error -- strict migration
          isReadOnly={isReadOnly}
          anchorName={anchorName}
          onClose={onCloseLog}
          onDataChanged={setData}
        />
      )}
    </WorkflowComponentWrapper>
  );
}

//#endregion

//#region Subcomponents

function DateTimePickerField({
  autofocus,
  date,
  inputType,
  label,
  style,
  onDateChanged,
  onInputTypeChanged,
}: {
  autofocus?: boolean;
  date: Date | null;
  inputType: DateOrTime | null;
  label: string;
  style?: StyleProp<ViewStyle>;
  onDateChanged: (newDate: Date) => void;
  onInputTypeChanged: (newInputType: DateOrTime | null) => void;
}) {
  const colors = useContext(ColorThemeContext);
  const [isClearingDateText, setIsClearingDateText] = useState(false);
  const [isClearingTimeText, setIsClearingTimeText] = useState(false);

  const dateText = useMemo(
    () => (isClearingDateText || isNil(date) ? undefined : moment(date).format('M/D/YY')),
    [date, isClearingDateText],
  );

  const timeText = useMemo(
    () => (isClearingTimeText || isNil(date) ? undefined : moment(date).format('h:mm a')),
    [date, isClearingTimeText],
  );

  const focusedStyle = useMemo(
    () => (isNil(inputType) ? {} : { borderColor: colors.input.borderActive }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputType],
  );

  const onDateBlur = useCallback(() => {
    setIsClearingDateText(false);
  }, []);

  const onDateFocus = useCallback(() => {
    onInputTypeChanged('date');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDateStringChanged = useCallback(
    (newDateString: string | null) => {
      if (isNil(newDateString)) {
        setIsClearingDateText(true);
      } else {
        setIsClearingDateText(false);
        onDateChanged(mergeCalendarDateIntoTarget(new Date(newDateString), date));
      }
    },
    [date, onDateChanged],
  );

  const onTimeBlur = useCallback(() => {
    setIsClearingTimeText(false);
  }, []);

  const onTimeFocus = useCallback(() => {
    onInputTypeChanged('time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTimeStringChanged = useCallback(
    (newTimeString: string | null) => {
      if (isNil(newTimeString)) {
        setIsClearingTimeText(true);
      } else {
        setIsClearingTimeText(false);
        onDateChanged(mergeTimeIntoTarget(new Date(newTimeString), date));
      }
    },
    [date, onDateChanged],
  );

  return (
    <MBView style={style}>
      <Text size="overline" weight="semibold" inactive style={cssStyles.editorSectionTitle}>
        {i18n('workflowTemplate.component.timeTracker.datumEditor.field.title', { label })}
      </Text>
      <MBView style={[styles.dateTimePickerFieldContainer, focusedStyle]}>
        <MBComboDatePickerField
          autoFocus={autofocus}
          formats={DATE_FORMATS}
          icon="calendar"
          label={i18n('workflowTemplate.component.timeTracker.datumEditor.field.date', { label })}
          style={{ flex: 1, marginEnd: grid(0.5) }}
          // @ts-expect-error -- strict migration
          value={dateText}
          onBlur={onDateBlur}
          // @ts-expect-error -- strict migration
          onChange={onDateStringChanged}
          onFocus={onDateFocus}
        />
        <MBComboDatePickerField
          formats={TIME_FORMATS}
          label={i18n('workflowTemplate.component.timeTracker.datumEditor.field.time', { label })}
          style={{ flex: 1, marginStart: grid(0.5) }}
          textAlign="right"
          // @ts-expect-error -- strict migration
          value={timeText}
          onBlur={onTimeBlur}
          // @ts-expect-error -- strict migration
          onChange={onTimeStringChanged}
          onFocus={onTimeFocus}
        />
      </MBView>
    </MBView>
  );
}

export function DatumEditor({
  datum: initialDatum,
  anchorName,
  onClose,
  onDatumChanged,
  onDatumDeleted,
}: {
  datum: TimeTrackerComponentDataValue | null;
  anchorName: string;
  onClose: VoidFunction;
  onDatumChanged: (newDatum: TimeTrackerComponentDataValue) => void;
  onDatumDeleted: VoidFunction;
}) {
  const isDesktop = useIsDesktop();

  const [state, setState] = useState<DatumEditorState>(() =>
    isNil(initialDatum)
      ? null
      : // @ts-expect-error -- strict migration
        sanitizeEndOfDateStringRange(initialDatum.start.timestamp, initialDatum.end?.timestamp),
  );

  const [selectionState, setSelectionState] = useState<DatumEditorSelectionState>({
    inputType: 'date',
    rangeSide: 'start',
  });

  const initialTitle = useMemo(
    () => (isNil(initialDatum) ? '' : makeTitleForDatum(initialDatum)),
    [initialDatum],
  );

  const datum = useMemo(() => {
    if (isNil(state)) {
      return initialDatum;
    }
    return {
      start: { ...initialDatum?.start, timestamp: state.start.toISOString() },
      end: { ...initialDatum?.end, timestamp: state.end.toISOString() },
    };
  }, [initialDatum, state]);

  const onDeletePressed = useCallback(() => {
    MBAlert.confirm({
      title: i18n('workflowTemplate.component.timeTracker.datumEditor.delete.confirmation.title'),
      message: i18n(
        'workflowTemplate.component.timeTracker.datumEditor.delete.confirmation.message',
        { title: initialTitle },
      ),
      confirmText: i18n(
        'workflowTemplate.component.timeTracker.datumEditor.delete.confirmation.confirm',
      ),
      confirmStyle: 'destructive',
      onConfirm: () => {
        onDatumDeleted();
        onClose();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDatumDeleted, onClose]);

  // @ts-expect-error -- strict migration
  const onRightIconPress = useCallback(() => onDatumChanged(datum), [datum, onDatumChanged]);

  const overflowMenu = useMemo(() => {
    if (isNil(initialDatum) || isDesktop) {
      return null;
    }
    return (
      <View paddingHorizontal={2}>
        <Menu align="right">
          <Menu.IconButton size="medium" iconSize={grid(4)} />
          <Menu.Content>
            <Menu.Item
              label={i18n('workflowTemplate.component.timeTracker.datumEditor.overflowMenu.delete')}
              onClick={onDeletePressed}
              danger
            />
          </Menu.Content>
        </Menu>
      </View>
    );
  }, [initialDatum, isDesktop, onDeletePressed]);

  const renderTitleBar = useCallback(
    () => (
      <AnchoredSheetTitleBar
        title={
          isNil(initialDatum)
            ? i18n('workflowTemplate.component.timeTracker.datumEditor.manual.title')
            : i18n('workflowTemplate.component.timeTracker.datumEditor.existing.title')
        }
        // @ts-expect-error -- strict migration
        rightButtonText={
          isEqual(datum, initialDatum) || isNil(datum)
            ? null
            : isNil(state)
            ? i18n('workflowTemplate.component.timeTracker.datumEditor.manual.commitButton')
            : i18n('workflowTemplate.component.timeTracker.datumEditor.existing.commitButton')
        }
        onRightIconPress={onRightIconPress}
        endContent={overflowMenu}
      />
    ),

    [datum, initialDatum, state, onRightIconPress, overflowMenu],
  );

  // Start date takes precedence: the user can always choose any start date, but can only choose an end date that is
  // after the start date, and choosing a start date after the end date will adjust the end date.

  const onStartDateChanged = useCallback(
    // @ts-expect-error -- strict migration
    (newDate: Date) => setState(sanitizeEndOfDateRange(newDate, state?.end)),
    [state],
  );

  const onEndDateChanged = useCallback(
    (newDate: Date) => {
      if (!isNil(state) && newDate < state.start) {
        return;
      }
      // @ts-expect-error -- strict migration
      setState(sanitizeStartOfDateRange(state.start, newDate));
    },
    [state],
  );

  const input = useMemo(() => {
    if (isNil(selectionState)) {
      return null;
    }
    return (
      <DatumEditorInput
        // @ts-expect-error -- strict migration
        endDate={state?.end}
        selectionState={selectionState}
        // @ts-expect-error -- strict migration
        startDate={state?.start}
        onEndDateChanged={onEndDateChanged}
        onStartDateChanged={onStartDateChanged}
        // @ts-expect-error -- strict migration
        setSelectionState={setSelectionState}
      />
    );
  }, [selectionState, state, onStartDateChanged, onEndDateChanged]);

  const startFieldInputType = useMemo(
    () => (selectionState?.rangeSide === 'start' ? selectionState.inputType : null),
    [selectionState],
  );

  const endFieldInputType = useMemo(
    () => (selectionState?.rangeSide === 'end' ? selectionState.inputType : null),
    [selectionState],
  );

  const onStartInputTypeChanged = useCallback(
    (newInputType: DateOrTime | null) => {
      if (isNil(newInputType)) {
        if (selectionState?.rangeSide !== 'end') {
          // @ts-expect-error -- strict migration
          setSelectionState(null);
        }
      } else {
        setSelectionState({ inputType: newInputType, rangeSide: 'start' });
      }
    },
    [selectionState],
  );

  const onEndInputTypeChanged = useCallback(
    (newInputType: DateOrTime | null) => {
      if (isNil(newInputType)) {
        if (selectionState?.rangeSide !== 'start') {
          // @ts-expect-error -- strict migration
          setSelectionState(null);
        }
      } else {
        setSelectionState({ inputType: newInputType, rangeSide: 'end' });
      }
    },
    [selectionState],
  );

  return (
    <AnchoredSheet
      anchorName={anchorName}
      initialSize="full"
      onClose={onClose}
      renderTitleBar={renderTitleBar}
      style={{ width: isDesktop ? grid(40) : undefined }}
    >
      <ScrollView keyboardShouldPersistTaps="handled">
        <MBView
          style={{
            padding: grid(1),
            ...(selectionState?.inputType === 'time' && styles.editorFormWhileInputtingTime),
          }}
        >
          <DateTimePickerField
            autofocus
            date={state?.start ?? null}
            inputType={startFieldInputType}
            label={i18n('workflowTemplate.component.timeTracker.datumEditor.start')}
            style={{ marginBottom: grid(1) }}
            onDateChanged={onStartDateChanged}
            onInputTypeChanged={onStartInputTypeChanged}
          />
          <DateTimePickerField
            date={state?.end ?? null}
            inputType={endFieldInputType}
            label={i18n('workflowTemplate.component.timeTracker.datumEditor.end')}
            onDateChanged={onEndDateChanged}
            onInputTypeChanged={onEndInputTypeChanged}
          />
        </MBView>
        {input}
        {!isNil(initialDatum) && isDesktop && <InlineDeleteButton onPress={onDeletePressed} />}
      </ScrollView>
    </AnchoredSheet>
  );
}

function DatumEditorInput({
  endDate,
  selectionState,
  startDate,
  onStartDateChanged,
  onEndDateChanged,
  setSelectionState,
}: {
  endDate: Date;
  selectionState: DatumEditorSelectionState;
  startDate: Date;
  onStartDateChanged: (newDate: Date) => void;
  onEndDateChanged: (newDate: Date) => void;
  setSelectionState: (newState: DatumEditorSelectionState | null) => void;
}) {
  const colors = useContext(ColorThemeContext);
  const isStart = selectionState.rangeSide === 'start';
  const handler = isStart ? onStartDateChanged : onEndDateChanged;
  const targetDate = isStart ? startDate : endDate;

  const input = (() => {
    switch (selectionState.inputType) {
      case 'date':
        return (
          <MBCalendar
            onSelectDate={date => handler(mergeCalendarDateIntoTarget(new Date(date), targetDate))}
            selectedDates={isNil(targetDate) ? [] : [targetDate.toISOString()]}
            // @ts-expect-error -- strict migration
            minDate={!isStart && moment(startDate).startOf('day').toDate().toISOString()}
          />
        );
      case 'time':
        return (
          <MBPlatformTimePicker
            android={{ display: 'clock' }}
            ios={{ display: 'spinner', textColor: colors.foregroundActive }}
            style={{ marginTop: grid(1), height: grid(32.5) }}
            value={targetDate}
            onChange={(time: Date) => handler(mergeTimeIntoTarget(time, targetDate))}
            onHide={() => setSelectionState(null)}
          />
        );
    }
  })();

  return <View paddingHorizontal={1}>{input}</View>;
}

function InlineDeleteButton({ onPress }: { onPress: () => void }) {
  const colors = useContext(ColorThemeContext);

  return (
    <>
      <MBView style={styles.divider} />
      <MBTouchableOpacity style={styles.inlineDeleteButton} onPress={onPress}>
        <MBIconV2 name="trash" size={24} color={colors.foregroundActive} />
        <Text size="body1" style={{ marginInlineStart: grid(1) }}>
          {i18n('common.delete')}
        </Text>
      </MBTouchableOpacity>
    </>
  );
}

function Label({
  children,
  hasTabularNumerals,
  icon,
  style,
  type,
}: {
  children: React.ReactNode;
  hasTabularNumerals?: boolean;
  icon: MBIconV2Name;
  style?: StyleProp<ViewStyle>;
  type: 'primary' | 'secondary';
}) {
  const colors = useContext(ColorThemeContext);

  const textComponent = useMemo(() => {
    switch (type) {
      case 'primary':
        return (
          <Text
            size="body1"
            style={hasTabularNumerals ? { fontVariantNumeric: 'tabular-nums' } : undefined}
          >
            {children}
          </Text>
        );
      case 'secondary':
        return (
          <Text
            size="body2"
            style={hasTabularNumerals ? { fontVariantNumeric: 'tabular-nums' } : undefined}
          >
            {children}
          </Text>
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, children]);

  return (
    <MBView style={[styles.labelContainer, style]}>
      <MBView style={styles.labelIcon}>
        <MBIconV2 name={icon} size={28} color={colors.accent} />
      </MBView>
      {textComponent}
    </MBView>
  );
}

function Log({
  component,
  data,
  isReadOnly,
  anchorName,
  onClose,
  onDataChanged,
}: {
  component: API.TimeTrackerComponent;
  data: TimeTrackerComponentData;
  isReadOnly: boolean;
  anchorName: string;
  onClose: VoidFunction;
  onDataChanged: (newData: TimeTrackerComponentData) => void;
}) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const [datumEditorState, setDatumEditorState] = useState<LogDatumEditorPresentationState | null>(
    null,
  );

  // We want to use a full-size sheet on mobile web because the sheet is not draggable.
  // This does not affect desktop web.
  const initialSize = 'full';

  const onDatumEditorClosed = useCallback(() => setDatumEditorState(null), []);

  const presentExistingDatumEditor = useCallback(
    (datum: TimeTrackerComponentDataValue, index: number) =>
      setDatumEditorState({ type: 'edit', datum, index }),
    [setDatumEditorState],
  );

  const logRows = useMemo(
    () =>
      data
        .map((datum, index) => ({ datum, index }))
        .filter(({ datum }) => !isNil(datum.end))
        // @ts-ignore -- strict migration: multiple diagnostics on next line
        .sort((a, b) => b.datum.end.timestamp.localeCompare(a.datum.end.timestamp))
        .map(({ datum, index }) => (
          <LogRow
            datum={datum}
            isReadOnly={isReadOnly}
            key={index}
            onPress={() => presentExistingDatumEditor(datum, index)}
          />
        )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, presentExistingDatumEditor],
  );

  const renderTitleBar = useCallback(() => {
    const exitIconProps = isDesktop
      ? null
      : {
          rightIcon: 'exit' as MBIconV2Name,
          rightIconColor: colors.foregroundActive,
          onRightIconPress: onClose,
        };
    return (
      <AnchoredSheetTitleBar
        title={i18n('workflowTemplate.component.timeTracker.log.title', {
          componentLabel: component.label,
        })}
        {...exitIconProps}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component.label]);

  const renderDatumEditor = useCallback(
    (state: LogDatumEditorPresentationState) => {
      switch (state.type) {
        case 'manual':
          return (
            <DatumEditor
              datum={null}
              anchorName={anchorName}
              onClose={onDatumEditorClosed}
              onDatumChanged={newDatum => {
                onDataChanged([...data, newDatum]);
                setDatumEditorState(null);
              }}
              onDatumDeleted={() => {}}
            />
          );
        case 'edit':
          return (
            <DatumEditor
              datum={state.datum}
              anchorName={anchorName}
              onClose={onDatumEditorClosed}
              onDatumChanged={newDatum => {
                const newData = [...data];
                newData[state.index] = newDatum;
                onDataChanged(newData);
                setDatumEditorState(null);
              }}
              onDatumDeleted={() => {
                const newData = [...data];
                newData.splice(state.index, 1);
                onDataChanged(newData);
                setDatumEditorState(null);
              }}
            />
          );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, onDatumEditorClosed, onDataChanged],
  );

  const onPressAddManualEntry = useCallback(() => setDatumEditorState({ type: 'manual' }), []);

  return (
    <NestedDesktopAwareBottomSheet<LogDatumEditorPresentationState>
      isDesktop={isDesktop}
      sheetProps={{
        anchorName,
        initialSize,
        onClose,
        renderTitleBar,
      }}
      state={datumEditorState}
      renderNestedChildren={renderDatumEditor}
    >
      <ScrollView contentContainerStyle={styles.logSheet}>
        <RNView onStartShouldSetResponder={() => true}>
          {logRows}
          {!isReadOnly && (
            <MBTouchableOpacity style={styles.manualEntryButton} onPress={onPressAddManualEntry}>
              <MBIconV2 name="plus" size={32} color={colors.foregroundActive} />
              <Text size="body1" style={{ marginInlineStart: grid(2) }}>
                Add New Manually
              </Text>
            </MBTouchableOpacity>
          )}
        </RNView>
      </ScrollView>
    </NestedDesktopAwareBottomSheet>
  );
}

function LogRow({
  datum,
  isReadOnly,
  onPress,
}: {
  datum: TimeTrackerComponentDataValue;
  isReadOnly: boolean;
  onPress: VoidFunction;
}) {
  const durationString = useMemo(() => makeTitleForDatum(datum), [datum]);
  const endTitle = useMemo(
    () => datum.end && formatDateToDateTimeString(new Date(datum.end.timestamp)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datum.end?.timestamp],
  );
  const startTitle = useMemo(
    () => formatDateToDateTimeString(new Date(datum.start.timestamp)),
    [datum.start.timestamp],
  );

  return (
    // @ts-expect-error -- strict migration
    <MBTouchableOpacity onPress={isReadOnly ? null : onPress}>
      <MBView style={[styles.logRow, styles.card]}>
        <Label
          icon="time-stopwatch"
          type="secondary"
          hasTabularNumerals={true}
          style={{ marginBottom: grid(0.25) }}
        >
          {durationString}
        </Label>
        <MBView style={styles.dateRangeRow}>
          <Label icon="calendar" type="secondary" style={{ flex: 1 }}>
            {startTitle}
          </Label>
          {datum.end && (
            <Label icon="calendar" type="secondary" style={{ flex: 1 }}>
              {endTitle}
            </Label>
          )}
        </MBView>
      </MBView>
    </MBTouchableOpacity>
  );
}

function NestedDesktopAwareBottomSheet<State>({
  children,
  isDesktop,
  sheetProps,
  state,
  renderNestedChildren,
}: {
  children: React.ReactNode;
  isDesktop: boolean;
  sheetProps: Omit<AnchoredSheetProps, 'children'>;
  state: State | null;
  renderNestedChildren: (state: State) => React.ReactNode;
}) {
  const nestedChildren = isNil(state) ? null : renderNestedChildren(state);

  return (
    <>
      {!state || !isDesktop ? (
        <AnchoredSheet {...sheetProps}>
          {children}
          {state && nestedChildren}
        </AnchoredSheet>
      ) : (
        nestedChildren
      )}
    </>
  );
}

function Timer({
  component,
  data,
  isReadOnly,
  anchorName,
  onDataChanged,
  onShowLog,
}: {
  component: API.TimeTrackerComponent;
  data: TimeTrackerComponentData;
  isReadOnly: boolean;
  anchorName: string;
  onDataChanged: (newData: TimeTrackerComponentData) => void;
  onShowLog: VoidFunction;
}) {
  const colors = useContext(ColorThemeContext);
  const isRunning = isTimerRunning(data);

  return (
    <MBView style={styles.timerContainer}>
      <MBTouchableOpacity onPress={onShowLog}>
        <View style={{ anchorName }}>
          <MBView style={styles.timerRow}>
            <Text size="body1">
              <TimerText
                isRunning={isRunning}
                onTick={(now: Date) => calculateDurationFromTimeTrackerData(data, now)}
              />
            </Text>
            <MBIconV2 name="arrow-point-right" size={grid(2.5)} color={colors.foregroundActive} />
          </MBView>
        </View>
        {!isReadOnly && (
          <View row marginBottom={1.5} marginTop={0} marginLeft={-0.5}>
            <TimeTrackerButton
              component={component}
              data={data}
              isRunning={isRunning}
              size="small"
              onDataChanged={onDataChanged}
            />
          </View>
        )}
      </MBTouchableOpacity>
    </MBView>
  );
}

//#endregion

//#region Helpers

function formatDateToDateTimeString(date: Date): string {
  return moment(date).format('MMM D, h:mm a');
}

function makeTitleForDatum(datum: TimeTrackerComponentDataValue): string {
  const start = new Date(datum.start.timestamp);
  const end = isNil(datum.end) ? new Date() : new Date(datum.end.timestamp);
  return formatMillisecondsToDurationString(end.getTime() - start.getTime(), false);
}

function mergeCalendarDateIntoTarget(source: Date, target: Date | null): Date {
  if (isNil(target)) {
    const roundedSource = new Date(source);
    roundedSource.setHours(new Date().getHours() + 1, 0, 0, 0);
    return roundedSource;
  } else {
    const newDate = new Date(target);
    newDate.setFullYear(source.getFullYear(), source.getMonth(), source.getDate());
    return newDate;
  }
}

function mergeTimeIntoTarget(source: Date, target: Date | null): Date {
  if (isNil(target)) {
    return source;
  }
  const newDate = new Date(target);
  newDate.setHours(
    source.getHours(),
    source.getMinutes(),
    source.getSeconds(),
    source.getMilliseconds(),
  );
  return newDate;
}

function sanitizeEndOfDateRange(start: Date, end: Date | null): { start: Date; end: Date } {
  return {
    start,
    end:
      isNil(end) || end < start ? new Date(start.getTime() + DEFAULT_DATE_RANGE_INTERVAL_MS) : end,
  };
}

function sanitizeEndOfDateStringRange(
  start: ISOString,
  end: ISOString | null,
): { start: Date; end: Date } {
  return sanitizeEndOfDateRange(new Date(start), isNil(end) ? null : new Date(end));
}

function sanitizeStartOfDateRange(start: Date | null, end: Date): { start: Date; end: Date } {
  return {
    start:
      isNil(start) || end < start
        ? new Date(end.getTime() - DEFAULT_DATE_RANGE_INTERVAL_MS)
        : start,
    end,
  };
}

//#endregion

//#region Styles

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    borderRadius: grid(1),
    boxShadow: '2px 2px 8px 0 rgba(0,0,0,0.1)',
  },
  timerContainer: {
    paddingRight: grid(1),
  },
  dateRangeRow: {
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: grid(3),
    justifyContent: 'space-between',
  },
  dateTimePickerFieldContainer: {
    borderColor: 'transparent',
    borderRadius: grid(1.5),
    borderWidth: 1,
    flexDirection: 'row',
    padding: grid(0.5),
  },
  divider: {
    backgroundColor: Colors.v2.gray10,
    height: 1,
    marginVertical: grid(1),
    marginHorizontal: grid(2),
  },
  editorFormWhileInputtingTime: {},
  inlineDeleteButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: grid(1),
    paddingHorizontal: grid(3),
    paddingVertical: grid(1.25),
  },
  labelContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  labelIcon: {
    marginEnd: grid(0.5),
  },
  logRow: {
    flexDirection: 'column',
    marginBottom: grid(1),
    paddingHorizontal: grid(2),
    paddingVertical: grid(1),
  },
  logSheet: {
    paddingBottom: grid(2),
    paddingHorizontal: grid(1.25),
    paddingTop: grid(1),
  },
  manualEntryButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingHorizontal: grid(4),
    paddingVertical: grid(2),
  },
  tabularNumerals: {
    fontVariant: ['tabular-nums'],
  },
  timerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: grid(1.25),
    // match layout metrics of text inputs
    minHeight: grid(5),
    marginVertical: -1,
  },
});

const cssStyles: Record<string, CSSProperties> = {
  editorSectionTitle: {
    marginBottom: grid(1),
    paddingInline: grid(2),
  },
};

//#endregion
